/**
 * Article rédigé le 2026-09-06 à partir de sources web ouvertes et vérifiées automatiquement
 * (6 URL testées, révisé après contre-vérification des sources). Daté au moment des faits : 2024-11-07.
 * À relire avant/après mise en ligne. Format : voir src/blog/README.md
 */
export default {
  "slug": "ccas-besancon-rendez-vous-age-ia-micropolis-2024",
  "title": "À Micropolis, le CCAS de Besançon initie ses aînés à l'intelligence artificielle",
  "excerpt": "Le 5 novembre 2024, les Rendez-vous de l'âge du CCAS de Besançon ont consacré une journée entière à l'IA : deux conférences, un quiz sur la sécurité numérique et une diffusion en ligne.",
  "seoTitle": "Rendez-vous de l'âge : le CCAS initie à l'IA",
  "seoDescription": "Le 5 novembre 2024, le CCAS de Besançon a réuni les seniors à Micropolis autour de l'intelligence artificielle : deux conférences, un quiz et des vidéos.",
  "category": "collectivites",
  "tags": [
    "CCAS de Besançon",
    "Rendez-vous de l'âge",
    "Micropolis",
    "Inclusion numérique",
    "Anne Vignot"
  ],
  "author": "jimmy",
  "publishedAt": "2024-11-07",
  "updatedAt": "2024-11-07",
  "featured": false,
  "cover": {
    "image": "/img/blog/parc-des-expositions-de-besancon.jpg",
    "credit": "Photo : le parc des expositions Micropolis à Besançon — Arnaud 25, CC0, via Wikimedia Commons",
    "alt": "La façade vitrée du parc des expositions Micropolis à Besançon, vue depuis son parking"
  },
  "content": [
    {
      "type": "lead",
      "text": "Mardi 5 novembre, le Centre communal d'action sociale (CCAS) de Besançon a réuni ses aînés à [Micropolis](/blog/micronora-2024-microns-or-jumeau-numerique) pour l'édition 2024 des Rendez-vous de l'âge, placée cette année sous le signe de l'intelligence artificielle. Conférences le matin, quiz sur la sécurité numérique l'après-midi, chorale participative en clôture : l'établissement a choisi de traiter un sujet réputé technique avec un public qu'on associe rarement à l'IA. Retour sur une journée qui éclaire la manière dont le CCAS, outil principal de la politique sociale de la Ville, aborde l'[inclusion numérique](/blog/besancon-numerique-demarches-open-data-fibre-inclusion)."
    },
    {
      "type": "keyfacts",
      "title": "En bref",
      "facts": [
        {
          "label": "Quoi",
          "value": "Les Rendez-vous de l'âge 2024, journée consacrée à l'intelligence artificielle et au numérique"
        },
        {
          "label": "Quand",
          "value": "Mardi 5 novembre 2024, de 9h15 (ouverture) à 16h30 (clôture musicale)"
        },
        {
          "label": "Où",
          "value": "Micropolis, parc des expositions de Besançon"
        },
        {
          "label": "Qui",
          "value": "CCAS de Besançon, présidé par la maire Anne Vignot ; conférenciers Stéphan Le Doaré et Sophie Othman"
        },
        {
          "label": "Pour qui",
          "value": "Les seniors bisontins ; deux vidéos de la journée sont en ligne sur la chaîne YouTube du CCAS"
        }
      ]
    },
    {
      "type": "h2",
      "text": "Une journée entière pour apprivoiser l'IA"
    },
    {
      "type": "p",
      "text": "Le programme annoncé par [macommune.info](https://www.macommune.info/un-evenement-sur-lintelligence-artificielle-a-besancon/) donnait le ton : l'ouverture était assurée dès 9h15 par Anne Vignot, maire de Besançon et présidente du CCAS, avant deux conférences matinales, un quiz à 15h, une allocution de l'adjointe Sylvie Wanlin à 16h15 et, pour finir, un « Choraoké » animé par Maxime Nicolas à 16h30 [^1]. L'article précisait aussi que la journée serait retransmise en léger différé sur Facebook et YouTube si elle affichait complet [^1]."
    },
    {
      "type": "p",
      "text": "La diffusion en ligne, elle, a bien eu lieu. La chaîne YouTube du CCAS de Besançon a diffusé puis laissé en ligne deux vidéos, l'une pour la matinée, diffusée en direct le 5 novembre alors que le programme n'annonçait qu'un léger différé, l'autre pour l'après-midi, avec en description la même phrase, sans ambiguïté : « Les rendez-vous de l'âge ont pour thème cette année l'intelligence artificielle » [^2][^3]. Les repères horodatés permettent de retrouver l'ouverture de la journée, chacune des conférences, le quiz et la clôture [^2][^3]. Autrement dit, un senior bisontin qui n'a pas pu se déplacer, ou qui souhaite revenir sur un passage, peut réécouter les conférences et le quiz."
    },
    {
      "type": "h2",
      "text": "Deux conférenciers, deux regards"
    },
    {
      "type": "p",
      "text": "Le premier intervenant, Stéphan Le Doaré, était programmé à 9h40 avec, en guise de titre, une question volontairement simple : « L'intelligence artificielle, vous connaissez ? ». Présenté comme diplômé en biologie, passé par les forces spéciales, consultant en systèmes d'information, romancier et conférencier en intelligence artificielle, il est l'auteur d'un deuxième roman qui aborde l'IA et le transhumanisme sous un angle social et prospectif [^1]. Sur son site, il indique diriger DSI Concept, une société de conseil marseillaise, et avoir publié deux romans, *Ré-Évolution* et *NeoCortex*, puis un essai, *Géopolitique de l'intelligence artificielle*, paru en novembre 2023 [^5]."
    },
    {
      "type": "p",
      "text": "À 11h20, Sophie Othman, maître de conférences en didactique des langues et numérique éducatif au Centre de linguistique appliquée (CLA) de Besançon, prenait le relais avec « L'intelligence artificielle, ça alors ! ». Sa conférence explorait les différentes facettes de l'IA conversationnelle et ses limites [^1]. Le programme la présentait comme « passionnée par l'intégration de l'intelligence artificielle (IA) dans l'enseignement des langues » [^1] ; faire intervenir une enseignante du CLA, établissement bisontin, donnait à la journée un ancrage universitaire local."
    },
    {
      "type": "h2",
      "text": "Un quiz pour naviguer en confiance"
    },
    {
      "type": "p",
      "text": "L'après-midi changeait de registre. À 15h, le quiz « L'univers numérique : comment y naviguer en toute confiance ? » passait en revue les risques du numérique et les bonnes pratiques à adopter [^1]. Le format, interactif, tranche avec la conférence magistrale : il s'agissait moins de comprendre le fonctionnement d'un modèle de langage que de tester ses connaissances sur les vols de données, les escroqueries en ligne, les fraudes bancaires et la vie privée, des experts étant annoncés pour apporter conseils, bonnes pratiques et bonnes adresses [^1]."
    },
    {
      "type": "p",
      "text": "Cette articulation entre culture générale de l'IA le matin et réflexes pratiques l'après-midi résume la logique de la journée :"
    },
    {
      "type": "ul",
      "items": [
        "**Comprendre** ce qu'est l'intelligence artificielle, d'où elle vient et vers quoi elle tend, avec Stéphan Le Doaré [^1].",
        "**Expérimenter** les usages et les limites des assistants conversationnels, avec Sophie Othman [^1].",
        "**Se protéger**, avec le quiz de l'après-midi consacré à la navigation en confiance [^1][^3].",
        "**Se retrouver** autour d'un moment collectif, le Choraoké, qui rappelle que ces rendez-vous sont d'abord une rencontre entre habitants [^1]."
      ]
    },
    {
      "type": "h2",
      "text": "Une tradition bisontine qui se renouvelle"
    },
    {
      "type": "p",
      "text": "Les Rendez-vous de l'âge ne sont pas une nouveauté. En novembre 2019 déjà, l'édition organisée à Micropolis avait enregistré 2 300 inscriptions, au point qu'une salle supplémentaire avait dû être louée ; le maire de l'époque, Jean-Louis Fousseret, y voyait le symbole de l'importance accordée à une population de seniors de plus en plus nombreuse et active [^4]. Fait notable, le programme de 2019 comportait déjà un atelier consacré à l'intelligence artificielle, parmi d'autres sujets [^4]. Cinq ans plus tard, le thème occupe la journée entière."
    },
    {
      "type": "p",
      "text": "Ce rendez-vous s'inscrit dans les missions du CCAS, établissement public administratif dont le conseil d'administration est présidé par la maire. Sa Maison des Séniors, ses services de soutien à domicile, ses quatre résidences autonomie et sa participation à l'ambition « Ville amie des aînés » portée par la Ville en font l'interlocuteur naturel des personnes âgées de la commune [^6]. Porter le sujet de l'IA devant ce public, plutôt que de le réserver aux entreprises ou aux étudiants, relève donc d'une continuité assez logique."
    },
    {
      "type": "callout",
      "tone": "info",
      "title": "Pourquoi ça compte pour Besançon",
      "text": "Les débats sur l'intelligence artificielle se tiennent le plus souvent dans les laboratoires, les incubateurs ou les salons professionnels. En consacrant les Rendez-vous de l'âge à ce thème, le CCAS fait entrer le sujet dans le champ de l'action sociale : l'enjeu n'est pas seulement de savoir ce qu'est l'IA, mais de ne pas laisser une partie de la population à l'écart des outils, et des arnaques, qui l'accompagnent. Les deux vidéos laissées en ligne sur YouTube prolongent cette démarche au-delà des murs de Micropolis [^2][^3]."
    },
    {
      "type": "h2",
      "text": "Ce qu'il faut retenir"
    },
    {
      "type": "p",
      "text": "En une journée, le CCAS de Besançon a proposé à ses aînés un parcours cohérent, de la découverte de l'IA à la protection contre les risques numériques, sans jargon et avec des intervenants identifiés. Aucune retranscription écrite des allocutions d'Anne Vignot et de Sylvie Wanlin n'a été retrouvée dans les sources publiques consultées ; les vidéos mises en ligne par le CCAS restent le meilleur moyen d'en prendre connaissance [^2][^3]. Reste à voir si cette acculturation d'une journée trouvera des prolongements plus réguliers dans l'offre d'accompagnement de la Maison des Séniors [^6]. C'est, en tout cas, une manière concrète de rappeler que l'inclusion numérique concerne tous les âges."
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "Un événement sur l'intelligence artificielle à Besançon",
      "publisher": "macommune.info",
      "url": "https://www.macommune.info/un-evenement-sur-lintelligence-artificielle-a-besancon/",
      "type": "press",
      "date": "2024-10-27"
    },
    {
      "id": 2,
      "title": "Rendez-vous de l'âge 2024 matin",
      "publisher": "CCAS de Besançon (chaîne YouTube)",
      "url": "https://www.youtube.com/watch?v=gdcW0cAtD5w",
      "type": "official",
      "date": "2024-11-05"
    },
    {
      "id": 3,
      "title": "Rendez-vous de l'âge 2024 après midi : quiz",
      "publisher": "CCAS de Besançon (chaîne YouTube)",
      "url": "https://www.youtube.com/watch?v=52tzAEKrQ4w",
      "type": "official",
      "date": "2024-11-06"
    },
    {
      "id": 4,
      "title": "Les rendez-vous de l'âge, ce jeudi à Micropolis Besançon",
      "publisher": "macommune.info",
      "url": "https://www.macommune.info/agenda/les-rendez-vous-de-lage-ce-jeudi-a-micropolis-besancon/",
      "type": "press",
      "date": "2019-11-21"
    },
    {
      "id": 5,
      "title": "Stéphan Le Doaré, essayiste – Géopolitique de l'IA",
      "publisher": "ledoare.com",
      "url": "https://www.ledoare.com/",
      "type": "web",
      "date": ""
    },
    {
      "id": 6,
      "title": "Centre Communal d'Action Sociale de Besançon",
      "publisher": "Ville de Besançon (besancon.fr)",
      "url": "https://www.besancon.fr/la-ville/ccas-de-besancon/",
      "type": "official",
      "date": ""
    }
  ]
};
